import * as _ from 'lodash';
import Initialiser from 'initialiser';
import LogRocket from 'logrocket';
import chatlio from 'utils/chatlio';
import { cloneDeep } from 'utils/immutable';
import Mixpanel from 'utils/mixpanel';
import Hubspot from 'utils/hubspot';
import SocketClient from 'utils/socket-client';
import api from 'utils/api';
import store from 'ducks/configureStore';
import constants from 'utils/constants';
import { startFetchUser, stopFetchUser, startRecoverPassword, stopRecoverPassword } from 'ducks/modules/fetching';
import { pushToast, dismissAllToasts } from 'ducks/modules/toaster';

// ACTION TYPES
const USER_LOGIN = 'USER_LOGIN';
const USER_LOGOUT = 'USER_LOGOUT';

// SYNC ACTION CREATORS
export function login(user) {
    return { type: USER_LOGIN, user };
}
export function doLogout() {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('ignore_events');
    localStorage.removeItem('matching_dashboard');
    Initialiser.history.push('/login', Initialiser.history.location.state);
    heap.resetIdentity();
    return { type: USER_LOGOUT, user: { access_token: null } };
}

// ASYNC ACTION CREATORS
export function logout(tokenExpired) {
    return async (dispatch) => {
        dispatch(stopFetchUser());
        dispatch(dismissAllToasts());
        if (tokenExpired === true) {
            dispatch(
                pushToast({
                    title: 'Your session has expired, please log in again.',
                    type: constants.ToastTypes.WARNING,
                    expires: constants.ToastDurations.LONG,
                }),
            );
        }
        dispatch(doLogout());
    };
}
export function authenticate(credentials) {
    return async (dispatch) => {
        try {
            dispatch(startFetchUser());
            const user = await api.authenticate(credentials);

            if (user && user.access_token) {
                // Best-effort third-party analytics identification. Any individual SDK
                // failing (missing init, network error) must NOT prevent the Redux
                // login dispatch — otherwise the user appears logged out and downstream
                // redirects (e.g. MusicUser.view → /draganddrop) never fire.
                try {
                    Mixpanel.identify(user);
                    chatlio.identify(user);
                    Hubspot.identify(user);

                    if (user.accepted_cookies === true) {
                        if (process.env.LOGROCKET_ID) {
                            LogRocket.identify(`${user.id}`, {
                                name: `${user.display_name}`,
                                email: `${user.email}`,
                                bk_user_id: `${user.bk_user_id}`,
                                user_type: user.source_type,
                            });
                        }
                        if (typeof window !== 'undefined' && window.heap) {
                            window.heap.identify(`${user.id}`);
                            window.heap.addUserProperties(
                                _.pick(user, [
                                    'bk_user_id',
                                    'name',
                                    'display_name',
                                    'username',
                                    'email',
                                    'first_login',
                                    'last_login',
                                    'seniority',
                                    'source_id',
                                    'source_type',
                                ]),
                            );
                        }
                    }
                    if (user.permissions.includes('notifications:GET')) {
                        SocketClient.connect();
                    }
                } catch (analyticsErr) {
                    console.warn('Third-party analytics identify failed (non-blocking):', analyticsErr);
                }
                localStorage.setItem('logged_in', new Date());
                localStorage.removeItem('matching_dashboard');
                return dispatch(login(user));
            }
        } catch (err) {
            console.error('Error authenticating: ', err);

            if (err) {
                dispatch(logout());
                switch (err.status) {
                    case constants.HttpCodes.UNAUTHORIZED:
                    case constants.HttpCodes.BAD_REQUEST:
                        return dispatch(
                            pushToast({
                                title: 'Invalid credentials.',
                                type: constants.ToastTypes.ERROR,
                                expires: constants.ToastDurations.SHORT,
                            }),
                        );
                    case constants.HttpCodes.SERVICE_UNAVAILABLE:
                        return dispatch(
                            pushToast({
                                title: 'Unable to reach server, please try again later.',
                                type: constants.ToastTypes.ERROR,
                                expires: constants.ToastDurations.LONG,
                            }),
                        );
                }
            }
            dispatch(stopFetchUser());
        }
    };
}
export function fetchUser() {
    return async (dispatch) => {
        try {
            dispatch(startFetchUser());
            const user = await api.fetchUser();

            if (user && user.permissions.includes('notifications:GET')) {
                SocketClient.connect();
            }
            if (user && user.access_token) {
                Mixpanel.identify(user);
                chatlio.identify(user);
                Hubspot.identify(user);
            }
            if (user) {
                // Loggin in also stops fetching the user
                return dispatch(login(user));
            }
            // If fetching the user failed then stop fetching it and log out
            dispatch(stopFetchUser());
            dispatch(logout());
        } catch (err) {
            console.error('Error fetching user: ', err);
            if (err && err.status === constants.HttpCodes.UNAUTHORIZED) {
                const tokenExpired = err.data.code === constants.ServerCodes.TOKEN_EXPIRED;
                if (!tokenExpired) {
                    dispatch(
                        pushToast({
                            title: 'Incorrect credentials.',
                            type: constants.ToastTypes.ERROR,
                            expires: constants.ToastDurations.SHORT,
                        }),
                    );
                }
                return dispatch(logout(tokenExpired));
            }
            dispatch(logout(true));
            dispatch(stopFetchUser());
        }
    };
}

export function recoverPassword(email) {
    return async (dispatch) => {
        dispatch(startRecoverPassword());

        try {
            await api.recoverPassword(email);
            dispatch(
                pushToast({
                    title: 'Thank you. If the address is registered with Blòkur, you will receive an email with instructions on how to reset your password',
                    type: constants.ToastTypes.INFO,
                    expires: constants.ToastDurations.SHORT,
                }),
            );
        } catch {
            dispatch(
                pushToast({
                    title: 'Unexpected error, please try again later.',
                    type: constants.ToastTypes.ERROR,
                    expires: constants.ToastDurations.SHORT,
                }),
            );
        }
        dispatch(stopRecoverPassword());
    };
}

// SELECTORS
export function selectUser() {
    return store.getState().user;
}

export function isUserMLC() {
    const userState = selectUser();
    return _.get(userState, 'bk_user_id') === 30113;
}

// REDUCER
export default function reducer(state = Initialiser.initialState.user, action) {
    switch (action.type) {
        case 'USER_LOGIN':
        case 'USER_LOGOUT':
            return cloneDeep(action.user);
        default:
            return state;
    }
}
